// @ts-nocheck
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@saas-boilerplate/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronDown, Loader2, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import {
  WorkspaceFlowShell,
  WorkspaceFlowMainBackdrop,
  WorkspaceFlowSegmentedStepper,
} from '@/components/workspace-flow-shell';

// Holistic, IANA-backed set covering the major populated zones, ordered roughly west→east.
// Labels lead with the city + UTC offset so the choice is unambiguous.
const TIMEZONE_OPTIONS = [
  { value: 'Etc/UTC', label: 'UTC - Coordinated Universal Time' },
  { value: 'Pacific/Honolulu', label: 'Honolulu (UTC−10)' },
  { value: 'America/Anchorage', label: 'Anchorage (UTC−09)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles · Pacific (UTC−08)' },
  { value: 'America/Denver', label: 'Denver · Mountain (UTC−07)' },
  { value: 'America/Chicago', label: 'Chicago · Central (UTC−06)' },
  { value: 'America/Mexico_City', label: 'Mexico City (UTC−06)' },
  { value: 'America/New_York', label: 'New York · Eastern (UTC−05)' },
  { value: 'America/Bogota', label: 'Bogotá (UTC−05)' },
  { value: 'America/Halifax', label: 'Halifax · Atlantic (UTC−04)' },
  { value: 'America/Sao_Paulo', label: 'São Paulo (UTC−03)' },
  { value: 'America/Argentina/Buenos_Aires', label: 'Buenos Aires (UTC−03)' },
  { value: 'Atlantic/Azores', label: 'Azores (UTC−01)' },
  { value: 'Europe/London', label: 'London · GMT/BST (UTC+00)' },
  { value: 'Europe/Lisbon', label: 'Lisbon (UTC+00)' },
  { value: 'Europe/Paris', label: 'Paris · Central European (UTC+01)' },
  { value: 'Europe/Berlin', label: 'Berlin (UTC+01)' },
  { value: 'Europe/Madrid', label: 'Madrid (UTC+01)' },
  { value: 'Africa/Lagos', label: 'Lagos (UTC+01)' },
  { value: 'Europe/Athens', label: 'Athens · Eastern European (UTC+02)' },
  { value: 'Africa/Johannesburg', label: 'Johannesburg (UTC+02)' },
  { value: 'Europe/Istanbul', label: 'Istanbul (UTC+03)' },
  { value: 'Europe/Moscow', label: 'Moscow (UTC+03)' },
  { value: 'Africa/Nairobi', label: 'Nairobi (UTC+03)' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+04)' },
  { value: 'Asia/Karachi', label: 'Karachi (UTC+05)' },
  { value: 'Asia/Kolkata', label: 'India · Kolkata (UTC+05:30)' },
  { value: 'Asia/Dhaka', label: 'Dhaka (UTC+06)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (UTC+07)' },
  { value: 'Asia/Shanghai', label: 'Shanghai · China (UTC+08)' },
  { value: 'Asia/Singapore', label: 'Singapore (UTC+08)' },
  { value: 'Asia/Hong_Kong', label: 'Hong Kong (UTC+08)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (UTC+09)' },
  { value: 'Asia/Seoul', label: 'Seoul (UTC+09)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10)' },
  { value: 'Pacific/Auckland', label: 'Auckland (UTC+12)' },
];

/** The user's actual browser timezone, falling back to UTC. Used as the default. */
function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC';
  } catch {
    return 'Etc/UTC';
  }
}

/** Matches post–sign-in onboarding: create (1) + policies, Slack, invites (2–4). */
const ONBOARDING_TOTAL_STEPS = 4;

const selectShell =
  'w-full appearance-none rounded-sm border-0 bg-surface-container-low px-4 py-4 text-foreground shadow-none ring-0 focus:outline-none focus:ring-2 focus:ring-primary/20';

const BENTO_IMAGE =
  'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&q=80&auto=format&fit=crop';

function extractDomain(email: string): string {
  const part = email.split('@')[1];
  return part ? part.toLowerCase().trim() : '';
}

type WorkspaceMembership = {
  workspace_id: string;
  name: string;
  company_name: string | null;
  role: string;
  last_active_at: string | null;
};

interface WorkspaceSelectProps {
  /**
   * 'login' (default): auto-select when there is exactly one membership; only show the picker
   * for genuine multi-workspace accounts. 'switch': the user explicitly asked to change
   * workspace (sidebar Switch workspace) - always show the picker, never bounce back.
   */
  mode?: 'login' | 'switch';
  onSelectWorkspace: (workspaceId: string) => void;
  onSignOut: () => void;
}

export function WorkspaceSelect({ mode = 'login', onSelectWorkspace, onSignOut }: WorkspaceSelectProps) {
  const [phase, setPhase] = useState<'loading' | 'pick' | 'create'>('loading');
  const [memberships, setMemberships] = useState<WorkspaceMembership[]>([]);

  // Create-form fields
  const [companyName, setCompanyName] = useState('');
  const [workspaceName, setWorkspaceName] = useState('');
  const [timezone, setTimezone] = useState(detectTimezone);
  // Language is fixed to English for now - we don't support other locales yet, so the
  // picker is hidden, but we still persist 'en' so existing settings shape is unchanged.
  const [language] = useState('en');
  const [isCreating, setIsCreating] = useState(false);

  const resetFields = useCallback(() => {
    setCompanyName('');
    setWorkspaceName('');
    setTimezone(detectTimezone());
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // 1. Load workspaces the user already belongs to, with company + role for the picker.
      const { data: { session: memberSession } } = await supabase.auth.getSession();
      const uid = memberSession?.user?.id;
      const { data: memberRows } = uid
        ? await supabase
            .from('workspace_members')
            .select('workspace_id, role, last_active_at, workspaces!inner(id, name, settings, companies(name))')
            .eq('user_id', uid)
            .eq('status', 'active')
        : { data: [] };
      if (cancelled) return;

      const workspaces: WorkspaceMembership[] = ((memberRows as any[]) || [])
        .filter((r) => !(r.workspaces?.settings?.deleted_at))
        .map((r) => ({
          workspace_id: r.workspace_id,
          name: r.workspaces?.name || 'Workspace',
          company_name: (Array.isArray(r.workspaces?.companies) ? r.workspaces?.companies[0] : r.workspaces?.companies)?.name || null,
          role: r.role || 'member',
          last_active_at: r.last_active_at || null,
        }))
        .sort((a, b) => String(b.last_active_at || '').localeCompare(String(a.last_active_at || '')));
      setMemberships(workspaces);

      // 2. Explicit switch: always show the picker (the auto-select here is what used to bounce
      //    users straight back into workspace #1 and made the switcher useless).
      if (mode === 'switch' && workspaces.length >= 1) {
        setPhase('pick');
        return;
      }

      // 2b. Login: exactly one membership auto-selects; two or more get the picker.
      if (workspaces.length === 1) {
        onSelectWorkspace(workspaces[0].workspace_id);
        return;
      }
      if (workspaces.length > 1) {
        setPhase('pick');
        return;
      }

      // 3. No memberships - try to auto-join via email domain
      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled) return;

      const email = session?.user?.email;
      if (email) {
        const { data: wsId } = await supabase.rpc('find_and_join_workspace_by_domain', { p_email: email });
        if (cancelled) return;
        if (wsId) {
          onSelectWorkspace(wsId as string);
          return;
        }
      }

      // 4. No match - show the create form
      if (!cancelled) {
        resetFields();
        setPhase('create');
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const handleCreateWorkspace = async () => {
    if (!companyName.trim()) { toast.error('Please enter a company name'); return; }
    if (!workspaceName.trim()) { toast.error('Please enter a workspace name'); return; }

    setIsCreating(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user?.email) { toast.error('Not authenticated'); setIsCreating(false); return; }

      // Guard: if a workspace for this domain already exists, join it instead
      try {
        const { data: existingWsId } = await supabase.rpc('find_and_join_workspace_by_domain', { p_email: user.email });
        if (existingWsId) {
          toast.success("Found your team's workspace - joining it now.");
          onSelectWorkspace(existingWsId as string);
          return;
        }
      } catch {
        // domain-check failure is non-blocking; continue to create
      }

      const domain = extractDomain(user.email);
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id || user.id;

      // Create company
      const { data: newCompany, error: companyError } = await supabase
        .from('companies')
        .insert({ name: companyName.trim(), created_by: userId })
        .select()
        .single();

      if (companyError || !newCompany) {
        toast.error('Failed to create company', { description: companyError?.message });
        setIsCreating(false);
        return;
      }

      // Create workspace
      const { data: workspace, error: workspaceError } = await supabase
        .from('workspaces')
        .insert({
          name: workspaceName.trim(),
          created_by: userId,
          workspace_domain: domain,
          company_id: newCompany.id,
          settings: { default_timezone: timezone, default_language: language },
        })
        .select()
        .single();

      if (workspaceError || !workspace) {
        toast.error('Failed to create workspace', { description: workspaceError?.message });
        setIsCreating(false);
        return;
      }

      // Add creator as admin
      const { error: memberError } = await supabase.from('workspace_members').upsert(
        { workspace_id: workspace.id, user_id: user.id, role: 'admin', status: 'active' },
        { onConflict: 'workspace_id,user_id' },
      );

      if (memberError) {
        toast.error('Failed to add you to the workspace', { description: memberError.message });
        setIsCreating(false);
        return;
      }

      toast.success('Workspace created!');
      onSelectWorkspace(workspace.id);
    } catch (err: any) {
      toast.error('Something went wrong', { description: err?.message });
      setIsCreating(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (phase === 'loading') {
    return (
      <WorkspaceFlowShell onSignOut={onSignOut} fabTooltip="Setting up your workspace…">
        <main className="relative flex min-h-[50vh] flex-col items-center justify-center px-6 py-24">
          <WorkspaceFlowMainBackdrop />
          <Loader2 className="relative z-10 h-10 w-10 animate-spin text-primary" />
          <p className="relative z-10 mt-4 text-sm text-muted-foreground">
            Setting up your workspace…
          </p>
        </main>
      </WorkspaceFlowShell>
    );
  }

  if (phase === 'pick') {
    return (
      <WorkspaceFlowShell onSignOut={onSignOut} fabTooltip="Choose the workspace to open.">
        <main className="relative flex flex-col items-center px-6 py-12 md:py-16">
          <WorkspaceFlowMainBackdrop />
          <section className="w-full max-w-xl">
            <div className="mb-8 text-center">
              <h1 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
                Choose a workspace
              </h1>
              <p className="mx-auto mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
                {memberships.length === 1
                  ? 'You belong to one workspace. Open it, or create another.'
                  : `You belong to ${memberships.length} workspaces. Pick the one to open.`}
              </p>
            </div>
            <div className="onboarding-glass-panel relative overflow-hidden rounded-xl border border-white/40 p-4 shadow-2xl shadow-amber-900/5 md:p-6">
              <div className="relative z-10 space-y-2">
                {memberships.map((m) => (
                  <button
                    key={m.workspace_id}
                    type="button"
                    onClick={() => onSelectWorkspace(m.workspace_id)}
                    className="flex w-full items-center justify-between gap-3 rounded-lg border border-border/60 bg-white/70 px-4 py-3 text-left transition-colors hover:border-primary/50 hover:bg-primary/5"
                  >
                    <div className="min-w-0">
                      <p className="truncate font-medium text-foreground">{m.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {[m.company_name, m.role === 'admin' ? 'Admin' : 'Member']
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <ChevronDown className="h-4 w-4 shrink-0 -rotate-90 text-muted-foreground" />
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => {
                    resetFields();
                    setPhase('create');
                  }}
                  className="w-full rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground transition-colors hover:border-primary/50 hover:text-foreground"
                >
                  Create a new workspace
                </button>
              </div>
            </div>
          </section>
        </main>
      </WorkspaceFlowShell>
    );
  }

  return (
    <WorkspaceFlowShell
      onSignOut={onSignOut}
      fabTooltip="Create your workspace to get started."
    >
      <main className="relative flex flex-col items-center px-6 py-12 md:py-16">
        <WorkspaceFlowMainBackdrop />

        <section className="w-full max-w-5xl">
          <div className="mb-10 text-center md:mb-12">
            {memberships.length > 0 && (
              <button
                type="button"
                onClick={() => setPhase('pick')}
                className="mb-4 text-sm text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
              >
                Back to your workspaces
              </button>
            )}
            <h1 className="font-display text-4xl font-light tracking-tight text-foreground md:text-5xl">
              Create your workspace
            </h1>
            <p className="mx-auto mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
              Set up your company, workspace, and defaults - you can invite others after you sign in.
            </p>
          </div>
        </section>

        <section className="w-full max-w-2xl">
          <div className="mb-8 flex justify-center px-2">
            <WorkspaceFlowSegmentedStepper
              current={1}
              total={ONBOARDING_TOTAL_STEPS}
              className="max-w-[280px]"
            />
          </div>

          <div className="onboarding-glass-panel relative overflow-hidden rounded-xl border border-white/40 p-8 shadow-2xl shadow-amber-900/5 md:p-12">
            <div
              className="onboarding-primary-gradient absolute -right-12 -top-12 h-48 w-48 rounded-full opacity-10 blur-3xl"
              aria-hidden
            />
            <div className="relative z-10 space-y-8">
              <div className="space-y-2">
                <Label
                  htmlFor="ws-company-name"
                  className="font-display text-sm font-medium text-muted-foreground"
                >
                  Company name
                </Label>
                <Input
                  id="ws-company-name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g. Acme Inc."
                  disabled={isCreating}
                  className="h-12 border-border/60 bg-white/80"
                />
              </div>

              <div className="space-y-2">
                <Label
                  htmlFor="ws-name"
                  className="font-display text-sm font-medium text-muted-foreground"
                >
                  Workspace name
                </Label>
                <Input
                  id="ws-name"
                  value={workspaceName}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  placeholder="e.g. Upstand"
                  disabled={isCreating}
                  className="h-12 border-border/60 bg-white/80"
                />
              </div>

              <div className="space-y-3">
                <Label
                  htmlFor="ws-tz"
                  className="block px-0.5 font-display text-sm font-medium tracking-wide text-muted-foreground"
                >
                  Default time zone
                </Label>
                <div className="group relative">
                  <select
                    id="ws-tz"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    disabled={isCreating}
                    className={selectShell}
                  >
                    {/* Guarantee the detected zone is always selectable, even if it's not
                        one of the curated options above. */}
                    {!TIMEZONE_OPTIONS.some((o) => o.value === timezone) && (
                      <option value={timezone}>{timezone.replace(/_/g, ' ')}</option>
                    )}
                    {TIMEZONE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-muted-foreground">
                    <ChevronDown className="h-4 w-4 opacity-70" />
                  </div>
                  <div className="absolute bottom-0 left-0 h-0.5 w-full bg-outline-variant/30 transition-colors group-focus-within:bg-primary" />
                </div>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  disabled={isCreating || !companyName.trim() || !workspaceName.trim()}
                  onClick={handleCreateWorkspace}
                  className="onboarding-primary-gradient inline-flex min-h-[48px] items-center justify-center rounded-lg px-10 py-3 font-display text-base font-semibold text-white shadow-xl shadow-amber-900/10 transition-all hover:opacity-90 active:scale-[0.98] disabled:opacity-50"
                >
                  {isCreating ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Create & enter'}
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="mt-20 grid w-full max-w-5xl grid-cols-1 gap-8 md:grid-cols-3">
          <div className="group relative aspect-video h-64 overflow-hidden rounded-lg bg-surface-container-low md:col-span-2 md:aspect-auto md:h-80">
            <img
              src={BENTO_IMAGE}
              alt=""
              className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <p className="font-display text-lg font-light">Collaborative Harmony</p>
              <p className="text-sm opacity-90">Design for focus and connection</p>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center space-y-4 rounded-lg bg-secondary-container/90 p-8 text-center">
            <div className="onboarding-primary-gradient flex h-16 w-16 items-center justify-center rounded-full shadow-lg shadow-amber-700/20">
              <Leaf className="h-8 w-8 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <h3 className="font-display text-xl text-headline">Digital Peace</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Upstand.ai removes the friction of coordination, giving you time back to think.
              </p>
            </div>
          </div>
        </section>
      </main>
    </WorkspaceFlowShell>
  );
}
